import React from "react";
import { Box, Button, Typography, Container, useTheme, useMediaQuery } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Full-screen non-fundus error component
 * Shows when image is detected as non-fundus
 * - 1st attempt: Standard error message
 * - 2nd attempt: "We are still unable to detect a valid image. Please try once again."
 * - 3rd attempt: Shows "The AI system cannot infer from this image" with referral option
 * - Similarity score >= 80 (3rd attempt for that eye): "These images may not give reliable AI results." + referral (same 1→2→3 flow as non-fundus)
 */
const NonFundusError = ({ onReupload, onProceed, attemptNumber = 1, isSimilarityScoreIssue = false }) => {
  const isThirdAttempt = attemptNumber >= 3;
  const isSecondAttempt = attemptNumber === 2;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <div style={{ 
      width: "100%", 
      height: "100vh", 
      display: "flex", 
      justifyContent: "center", 
      alignItems: "center",
      position: "fixed",
      top: 0,
      left: 0,
      zIndex: 9999,
      backgroundColor: "rgba(249, 252, 255, 1)"
    }}>
      <Container sx={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center", 
        justifyContent: "center",
        textAlign: "center",
        maxWidth: { xs: "90%", sm: "500px", md: "480px" },
        padding: { xs: "16px", sm: "20px" },
        width: "100%"
      }}>
        {/* Error Icon and Message */}
        {isThirdAttempt ? (
          // Third attempt: Yellow warning banner
          <Box
            sx={{
              background: "#FFF9E6",
              border: "1px solid #FFE082",
              borderRadius: "8px",
              padding: { xs: "16px", sm: "20px" },
              marginBottom: { xs: "16px", sm: "20px" },
              display: "flex",
              alignItems: "center",
              gap: { xs: "10px", sm: "12px" },
              width: "100%"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <img src="/assests/images/exclamation.svg" alt="" style={{ width: "32px", height: "32px" }} />
            </Box>
            <Typography 
              fontSize={{ xs: "14px", sm: "16px" }}
              fontWeight={500} 
              sx={{ color: "#E65100", flex: 1, wordBreak: "break-word" }}
            >
              {isSimilarityScoreIssue 
                ? "These images may not give reliable AI results."
                : "The AI system cannot infer from this image"
              }
            </Typography>
          </Box>
        ) : (
          // First and second attempt: Red error banner
          <Box
            sx={{
              background: "#FFEBEE",
              border: "1px solid #FFCDD2",
              borderRadius: "8px",
              padding: { xs: "16px", sm: "20px" },
              marginBottom: { xs: "16px", sm: "20px" },
              display: "flex",
              alignItems: "center",
              gap: { xs: "10px", sm: "12px" },
              width: "100%"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <img src="/assests/images/Cross.svg" alt="" style={{ width: "32px", height: "32px" }} />
            </Box>
            <Typography 
              fontSize={{ xs: "14px", sm: "16px" }}
              fontWeight={500} 
              sx={{ 
                color: "#D32F2F", 
                flex: 1,
                wordBreak: "break-word",
                lineHeight: 1.4
              }}
            >
              Uploaded image is detected as an invalid image
            </Typography>
          </Box>
        )}

        {/* Additional message for second attempt */}
        {isSecondAttempt && (
          <Typography 
            fontSize={{ xs: "13px", sm: "14px" }}
            fontWeight={400}
            sx={{ 
              color: "#404040",
              marginBottom: { xs: "16px", sm: "20px" },
              textAlign: "center",
              width: "100%",
              paddingX: { xs: "8px", sm: "0" }
            }}
          >
            We are still unable to detect a valid image. Please try once again.
          </Typography>
        )}

        {/* Third attempt: Referral message */}
        {isThirdAttempt && (
          <Typography 
            fontSize={{ xs: "13px", sm: "14px" }}
            fontWeight={400}
            sx={{ 
              color: "#404040",
              marginBottom: { xs: "16px", sm: "20px" },
              textAlign: "center",
              width: "100%",
              paddingX: { xs: "8px", sm: "0" }
            }}
          >
            To ensure patient well-being, please proceed with a referral for this patient.
          </Typography>
        )}

        {/* Action Buttons */}
        <Box sx={{ 
          display: "flex", 
          flexDirection: isThirdAttempt ? { xs: "column", sm: "row" } : "column", 
          gap: { xs: "10px", sm: "12px" }, 
          width: "100%", 
          alignItems: "center",
          justifyContent: "center"
        }}>
          {isThirdAttempt && (
            <Button
              variant="outlined"
              onClick={onProceed}
              sx={{
                border: "1px solid #0D1E83",
                borderRadius: "8px",
                color: "#0D1E83",
                textTransform: "none",
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: 500,
                padding: { xs: "10px 20px", sm: "12px 32px" },
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "auto", sm: "200px" },
                '&:hover': {
                  border: "1px solid #0D1E83",
                  background: "rgba(13, 30, 131, 0.05)"
                }
              }}
            >
              Proceed with referral
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onReupload}
            endIcon={<RefreshIcon />}
            sx={{
              background: 'linear-gradient(270deg, #051572 7.32%, #0820B4 92.68%)',
              borderRadius: "8px",
              color: "#FFF",
              textTransform: "none",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 500,
              padding: { xs: "8px 12px", sm: "8px 16px" },
              width: "fit-content",
              minWidth: "unset",
              maxWidth: "fit-content",
              flex: "none",
              '&:hover': {
                background: 'linear-gradient(270deg, #041461 7.32%, #071A9F 92.68%)',
              }
            }}
          >
            Re-upload
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default NonFundusError;

